/** Small pieces shared by the health screens: data hooks, labels and the hold line. */
import type { ReactNode } from "react";
import { UNIT_LABELS, type Quantity } from "../../../contract";
import { formatDate, formatNumber } from "../../../web/lib/format";
import { plural, t } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { Icon, cx } from "../../../web/ui/kit";
import type { ProductInfo } from "../shared/dto";
import { ROUTES, SITES, daysLeft, type HealthHold, type Protocol, type ProtocolStep } from "../shared/logic";

export const API = "/m/health";

export function useProducts() {
  return useApi<{ items: ProductInfo[]; today: string }>(`${API}/products`);
}

export function useProtocols() {
  return useApi<{ items: Protocol[]; can_edit: boolean }>(`${API}/protocols`);
}

export const unitLabel = (unit: string) => t(UNIT_LABELS[unit] ?? unit);
export const qty = (q: Quantity | null | undefined) => (q ? `${formatNumber(q.value, 2)} ${unitLabel(q.unit)}` : "");
export const routeLabel = (r: string | undefined) => (r ? t(ROUTES[r as keyof typeof ROUTES] ?? r) : "");
export const siteLabel = (s: string | undefined) => (s ? t(SITES[s as keyof typeof SITES] ?? s) : "");

/** "2 mL SQ" or "1 mL per 22 lb, pour-on". */
export function stepDose(s: Pick<ProtocolStep, "dose" | "dose_per" | "route">): string {
  const dose = s.dose ? (s.dose_per ? t("{{dose}} per {{weight}}", { dose: qty(s.dose), weight: qty(s.dose_per) }) : qty(s.dose)) : "";
  return [dose, routeLabel(s.route)].filter(Boolean).join(" ");
}

export function onHandText(p: Pick<ProductInfo, "on_hand" | "unit">): string {
  return t("{{amount}} on hand", { amount: `${formatNumber(p.on_hand)} ${unitLabel(p.unit)}` });
}

/** One hold, the way a rancher reads it: until when, from what, how long. */
export function HoldLine({ hold, today, showAnimal }: { hold: HealthHold & { animal?: { label: string } }; today: string; showAnimal?: boolean }) {
  const left = daysLeft(hold.until, today);
  return (
    <div className="health-hold">
      <span className="health-hold-icon"><Icon name="shieldAlert" size={20} /></span>
      <span className="health-hold-main">
        <strong>
          {showAnimal && hold.animal ? `${hold.animal.label} · ` : ""}
          {hold.kind === "meat" ? t("Meat hold until {{date}}", { date: formatDate(hold.until) }) : t("Milk hold until {{date}}", { date: formatDate(hold.until) })}
        </strong>
        <span className="muted">
          {[hold.product || t("Unknown product"), t("given {{date}}", { date: formatDate(hold.on) }),
            hold.days !== undefined ? plural(hold.days, "{{count}} day", "{{count}} days") : hold.hours !== undefined ? plural(hold.hours, "{{count}} hour", "{{count}} hours") : null,
            hold.source === "vet" ? t("vet's instructions") : hold.source === "label" ? t("from the label") : null,
            plural(left, "{{count}} day left", "{{count}} days left")].filter(Boolean).join(" · ")}
        </span>
      </span>
    </div>
  );
}

/** A labeled block used on health cards. */
export function Block({ title, tone, icon, children, action }: { title: ReactNode; tone?: "warn" | "bad" | "good" | "health"; icon?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={cx("health-block", tone && `tone-${tone}`)}>
      <div className="health-block-head">
        {icon && <span className="health-block-icon"><Icon name={icon} size={18} /></span>}
        <h3>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
